import { ITeamResponseSchema, ITeamUpdateSchema } from '@monetize/types/app';
import { useCallback } from 'react';
import { useUpdateTeam } from '../../../../../api/settingsService';

/**
 * The backend has no add/remove-member endpoint: membership is replaced
 * wholesale via PUT /api/teams/{id}. These helpers build that payload from the
 * RAW team (including deactivated users) so a save never silently drops
 * members that are merely hidden from the UI.
 */
export const getTeamUserIds = (team: ITeamResponseSchema): string[] =>
  team.users.map(({ id }) => id);

export const buildTeamUpdatePayload = (
  team: ITeamResponseSchema,
  overrides: Partial<Pick<ITeamUpdateSchema, 'name' | 'userIds'>> = {},
): ITeamUpdateSchema => ({
  name: overrides.name ?? team.name,
  type: team.type,
  owner: team.owner,
  userIds: overrides.userIds ?? getTeamUserIds(team),
});

export const getUserIdsAfterAdd = (
  team: ITeamResponseSchema,
  newUserIds: string[],
): string[] => Array.from(new Set([...getTeamUserIds(team), ...newUserIds]));

/** The owner can never be removed; change the owner first instead. */
export const getUserIdsAfterRemove = (
  team: ITeamResponseSchema,
  removedUserId: string,
): string[] => {
  if (removedUserId === team.owner) {
    return getTeamUserIds(team);
  }
  return getTeamUserIds(team).filter((userId) => userId !== removedUserId);
};

export const useTeamMembership = (team: ITeamResponseSchema | undefined) => {
  // Error toasts are the callers' responsibility (each already catches and
  // reports), so the global MutationCache toast is intentionally not enabled.
  const { mutateAsync: updateTeam, isPending } = useUpdateTeam({
    teamId: team?.id,
  });

  const addMembers = useCallback(
    async (newUserIds: string[]) => {
      if (!team || !newUserIds.length) {
        return;
      }
      await updateTeam(
        buildTeamUpdatePayload(team, {
          userIds: getUserIdsAfterAdd(team, newUserIds),
        }),
      );
    },
    [team, updateTeam],
  );

  const removeMember = useCallback(
    async (removedUserId: string) => {
      if (!team) {
        return;
      }
      await updateTeam(
        buildTeamUpdatePayload(team, {
          userIds: getUserIdsAfterRemove(team, removedUserId),
        }),
      );
    },
    [team, updateTeam],
  );

  const renameTeam = useCallback(
    async (name: string) => {
      if (!team) {
        return;
      }
      await updateTeam(buildTeamUpdatePayload(team, { name }));
    },
    [team, updateTeam],
  );

  return { addMembers, removeMember, renameTeam, isPending };
};
